import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Divider, Drawer, Form, Space, Tooltip } from "antd";
import { QualityLineProps, OrderProps } from "../../../interfaces/interfaces";
import { Datatable } from "../../functional";
import { QualityLinesTableColumns } from "../../../utils/tables/columns";
import { OrderLineForm } from "./OrderLineForm";
import { OrderForm } from "./OrderForm";
import {
  order_initial_state,
  order_line_initial_state,
} from "../../../utils/data";
import DataContext from "../../../context/DataContext";
import { useAlertPrevent, usePdf } from "../../../hooks";
import { RightOutlined, LeftOutlined } from "@ant-design/icons";

interface Props {
  open: boolean;
  onClickPrevious?: () => void;
  onClickNext?: () => void;
  onClose: () => void;
}

export const OrderDrawer = ({
  open,
  onClickPrevious,
  onClickNext,
  onClose,
}: Props) => {
  const [form] = Form.useForm();
  const {
    orderToEdit,
    handleCreateOrder,
    orderLineToEdit,
    onEditOrderLine,
    handleEditOrder,
  } = useContext(DataContext);
  const { modalAlertPrevent, openAlertPrevent } = useAlertPrevent();
  const { displayPdfFile, componentPdf } = usePdf<OrderProps>();
  const [values, setValues] = useState(order_initial_state);
  const [valuesOrderLine, setValuesOrderLine] = useState(
    order_line_initial_state
  );
  const [openQualityLines, setOpenQualityLines] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    orderToEdit && setValues(orderToEdit);
    return () => {
      setValues(order_initial_state);
    };
  }, [orderToEdit]);

  useEffect(() => {
    orderLineToEdit && setValuesOrderLine(orderLineToEdit);
    return () => {
      setValuesOrderLine(order_line_initial_state);
    };
  }, [orderLineToEdit]);

  const editOrderLine = (orderLineItem: QualityLineProps) => {
    setValues((prev) => {
      return {
        ...prev,
        QualityLines: prev.QualityLines.map((orderLine) => {
          if (orderLine.OID === orderLineItem.OID)
            return { ...orderLineItem, isCreated: false };
          return orderLine;
        }),
      };
    });
  };

  const addOrderLine = (orderLineItem: QualityLineProps) => {
    setValues((prev) => {
      return {
        ...prev,
        QualityLines: [
          ...prev.QualityLines,
          { ...orderLineItem, isCreated: true },
        ],
      };
    });
  };

  const handleSaveOrder = () => {
    form
      .validateFields()
      .then(() => {
        orderToEdit ? handleEditOrder(values) : handleCreateOrder(values);
      })
      .then(() => {
        setValues(order_initial_state);
        setHasChanged(false);
        onClose();
      })
      .catch((errorInfo) => {
        console.log("errorInfo ...", errorInfo);
      });
  };

  const handleAutoSave = () => {
    if (hasChanged) {
      openAlertPrevent();
      setHasChanged(false);
    } else {
      onClose();
    }
  };

  const getIndex = (array: QualityLineProps[], id: number | null) => {
    if (id === 0 && array.length > 0) return -1;
    if (array.length === 0) return 0;
    let indexArray = array.findIndex((item) => item.OID === id);
    return indexArray > 0 ? indexArray : 0;
  };

  return (
    <>
      <Drawer
        title={orderToEdit ? "Edit Order" : "Create new Order"}
        width={1000}
        onClose={handleAutoSave}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            handleAutoSave();
          }
        }}
        maskClosable={false}
        open={open}
        extra={
          <Space>
            {/* <Button
              type="primary"
              onClick={() =>
                displayPdfFile<OrderProps>({ source: "order", data: values })
              }
            >
              Print
            </Button> */}
            {orderToEdit && (
              <>
                <Tooltip title="Previous Record">
                  <Button
                    disabled={!onClickPrevious}
                    icon={<LeftOutlined />}
                    onClick={onClickPrevious}
                  ></Button>
                </Tooltip>
                <Tooltip title="Next Record">
                  <Button
                    disabled={!onClickNext}
                    icon={<RightOutlined />}
                    onClick={onClickNext}
                  ></Button>
                </Tooltip>
              </>
            )}
          </Space>
        }
        footer={
          <Space className="padding-all">
            {orderToEdit && (
              <Button onClick={onClose} danger>
                Cancel
              </Button>
            )}
            <Button htmlType="submit" type="primary" onClick={handleSaveOrder}>
              Save Order
            </Button>
          </Space>
        }
      >
        <OrderForm
          values={values}
          setValues={setValues}
          form={form}
          onClickField={() => {
            setHasChanged(true);
          }}
        />

        <Divider>
          <Button onClick={() => setOpenQualityLines(true)} type="primary">
            New Quality Check
          </Button>
        </Divider>
        <Card bordered hoverable className="card overflow_x">
          <Datatable<QualityLineProps>
            columns={QualityLinesTableColumns}
            data={values.QualityLines}
            rowKey="OID"
            onRowClick={(orderLine) => {
              onEditOrderLine(orderLine);
              setOpenQualityLines(true);
            }}
            isPaginated={false}
            allowSelection={false}
          />
        </Card>
      </Drawer>
      <OrderLineForm
        index={getIndex(values.QualityLines, valuesOrderLine.OID)}
        open={openQualityLines}
        valuesEdit={valuesOrderLine}
        orderId={values.OID}
        QualityLinesLength={values.QualityLines.length || 0}
        isEdition={!!orderLineToEdit}
        onClose={() => {
          onEditOrderLine(undefined);
          setOpenQualityLines(false);
        }}
        onEditOrderLine={editOrderLine}
        onAddOrderLine={addOrderLine}
      />
      <>{componentPdf}</>
      {modalAlertPrevent({
        type: "autosave",
        next: () => {
          handleSaveOrder();
        },
        cancel: () => {
          onClose();
          setValues(order_initial_state);
          setHasChanged(false);
        },
      })}
    </>
  );
};
